import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiError, banner as bannerApi, can, detailMessages, getUser,
  type BannerAppearance, type BannerState, type BannerTemplateIssue, type BannerTemplateListing,
} from '../../lib/api';
import { day } from '../../lib/format';
import { ErrorNote, Modal, useToast } from '../../components/ui';
import type { PointRow } from './types';
import { appearanceIssues, designLabel, sameAppearance, templateEdited, withDefaults, DEFAULT_APPEARANCE } from './banner/defaults';
import DesignCard from './banner/DesignCard';
import DesignEditor from './banner/DesignEditor';
import LayoutCard from './banner/LayoutCard';
import ButtonsCard from './banner/ButtonsCard';
import TextCard, { type Language } from './banner/TextCard';
import LookCard from './banner/LookCard';
import ReopenCard from './banner/ReopenCard';
import BehaviourCard from './banner/BehaviourCard';
import Regions from './banner/Regions';
import Preview from './banner/Preview';
import { useTemplateCheck } from './banner/useTemplateCheck';

/**
 * Website cookies > Banner: how one cookie banner looks and reads, with the
 * real banner beside the form. Save keeps a draft; Publish makes it live on the
 * next page load and bumps the version every cookie consent event records.
 *
 * The design (template) is a built-in structure or the author's own markup.
 * The editor takes the form column's place while open, so the preview stays
 * beside it; template rule issues block Save and Publish, what the preview
 * measures blocks Publish only.
 */

const UNSAVED = 'Unsaved banner changes are lost.';

type Template = BannerAppearance['template'];

export default function Banner({ points, onChanged, onDirty }: {
  points: PointRow[];
  onChanged: () => void;
  onDirty?: (dirty: boolean) => void;
}) {
  const toast = useToast();
  const editable = can(getUser(), 'cookies:configure');
  const banners = useMemo(() => points.filter((p) => p.channel === 'cookie'), [points]);
  const [bannerId, setBannerId] = useState('');
  const banner = banners.find((b) => b.id === bannerId) ?? banners[0] ?? null;

  const [state, setState] = useState<BannerState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<BannerAppearance>(DEFAULT_APPEARANCE);
  const [busy, setBusy] = useState<'save' | 'publish' | 'discard' | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [languages, setLanguages] = useState<Language[]>([{ code: 'en' }]);
  const [lang, setLang] = useState('en');

  // The design editor and the file round trip.
  const [editing, setEditing] = useState(false);
  const snapshot = useRef<Template | null>(null);
  const builtIns = useRef<BannerTemplateListing[] | null>(null);
  const [fileBusy, setFileBusy] = useState<'export' | 'import' | null>(null);
  const [importIssues, setImportIssues] = useState<string[]>([]);
  const [previewIssues, setPreviewIssues] = useState<BannerTemplateIssue[]>([]);

  /** The form as it stands on the server: the draft, which is the live copy when none is saved. */
  const baseline = (s: BannerState | null): BannerAppearance => {
    const defaults = withDefaults(s?.defaults);
    return withDefaults(s?.draft ?? s?.live, defaults);
  };

  async function load(id: string) {
    setLoadError(null);
    try {
      const s = await bannerApi.get(id);
      setState(s);
      setForm(baseline(s));
    } catch (err) {
      setState(null);
      setForm(DEFAULT_APPEARANCE);
      setLoadError((err as Error).message);
    }
  }

  useEffect(() => {
    if (!banner) return;
    setLogoUrl(null);
    setEditing(false);
    setImportIssues([]);
    void load(banner.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner?.id]);

  const dirty = state !== null && !sameAppearance(form, baseline(state));
  const issues = appearanceIssues(form);
  const { issues: templateIssues, checking } = useTemplateCheck(form.template, editing);
  const saveBlocked = issues.length > 0 || templateIssues.length > 0;
  const publishBlocked = saveBlocked || previewIssues.length > 0;
  const count = (n: number) => (n === 0 ? '' : ` · ${n} ${n === 1 ? 'issue' : 'issues'}`);

  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);
  useEffect(() => {
    if (!dirty) return;
    const guard = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = UNSAVED; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [dirty]);

  const update = (fn: (a: BannerAppearance) => BannerAppearance) => { if (editable) setForm(fn); };

  function pick(id: string) {
    if (dirty && !window.confirm(`${UNSAVED} Switch banner?`)) return;
    setBannerId(id);
  }

  async function saveDraft(): Promise<BannerState | null> {
    if (!banner) return null;
    setBusy('save');
    setImportIssues([]);
    try {
      const s = await bannerApi.saveDraft(banner.id, form);
      setState(s);
      setForm(baseline(s));
      toast('Draft saved.', 'ok');
      return s;
    } catch (err) {
      toast((err as Error).message, 'bad');
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    if (!banner) return;
    setBusy('publish');
    setImportIssues([]);
    try {
      if (dirty) await bannerApi.saveDraft(banner.id, form);
      const s = await bannerApi.publish(banner.id);
      setState(s);
      setForm(baseline(s));
      setPublishing(false);
      toast(`Banner v${s.version} published.`, 'ok');
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  async function discard() {
    if (!banner) return;
    setBusy('discard');
    setImportIssues([]);
    try {
      if (state?.hasDraft) {
        const s = await bannerApi.discardDraft(banner.id);
        setState(s);
        setForm(baseline(s));
      } else {
        setForm(baseline(state));
      }
      setDiscarding(false);
      setEditing(false);
      toast('Draft discarded.', 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  // ------------------------------------------------------------ the design

  /** The built-in designs, fetched once; null when the route is not there. */
  async function builtInList(): Promise<BannerTemplateListing[] | null> {
    if (builtIns.current) return builtIns.current;
    try {
      const list = await bannerApi.templates();
      builtIns.current = Array.isArray(list) ? list : [];
      return builtIns.current;
    } catch {
      return null;
    }
  }

  const builtIn = (base: Template['base']) => builtIns.current?.find((t) => t.key === base) ?? null;

  /** Open the editor on the current template; a built-in with no edits starts from its own source. */
  async function openEditor() {
    snapshot.current = form.template;
    if (!templateEdited(form.template)) {
      const list = await builtInList();
      const src = list?.find((t) => t.key === form.template.base);
      if (src) {
        update((x) => ({ ...x, template: { ...x.template, html: src.html, css: src.css } }));
      } else if (list === null) {
        toast('The built-in design could not be loaded from the server; the editor starts empty.', 'bad');
      }
    }
    setEditing(true);
  }

  /** Keep the edited template; an untouched built-in source goes back to being the built-in. */
  function applyEditor() {
    update((x) => {
      const t = x.template;
      const src = builtIn(t.base);
      if (t.base !== 'custom' && src && t.html === src.html && t.css === src.css && !t.name.trim()) {
        return { ...x, template: { ...t, html: '', css: '' } };
      }
      return x;
    });
    setEditing(false);
  }

  function discardEditor() {
    const t = snapshot.current;
    if (t) update((x) => ({ ...x, template: t }));
    setEditing(false);
  }

  /** The built-in design again: in the editor its source is shown, on the card the edits are dropped. */
  async function resetTemplate() {
    const base = form.template.base === 'custom' ? 'card' : form.template.base;
    if (editing) {
      const list = await builtInList();
      const src = list?.find((t) => t.key === base);
      update((x) => ({ ...x, template: { base, html: src?.html ?? '', css: src?.css ?? '', name: '' } }));
    } else {
      if (!window.confirm('The edits to the design are dropped. Reset?')) return;
      update((x) => ({ ...x, template: { base, html: '', css: '', name: '' } }));
    }
  }

  async function exportBanner() {
    if (!banner) return;
    if (dirty && !window.confirm('Unsaved changes are not in the file. Export the saved banner?')) return;
    setFileBusy('export');
    try {
      const filename = await bannerApi.exportFile(banner.id);
      toast(`${filename} saved.`, 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setFileBusy(null);
    }
  }

  async function importBanner(file: File) {
    if (!banner) return;
    if (dirty && !window.confirm(`${UNSAVED} Import the file?`)) return;
    setFileBusy('import');
    setImportIssues([]);
    try {
      let document: unknown;
      try {
        document = JSON.parse(await file.text());
      } catch {
        throw new Error(`${file.name} is not a JSON file.`);
      }
      const s = await bannerApi.importFile(banner.id, document);
      setState(s);
      setForm(baseline(s));
      setEditing(false);
      toast(`${file.name} imported into the draft.`, 'ok');
    } catch (err) {
      const messages = err instanceof ApiError ? detailMessages(err.details) : [];
      setImportIssues(messages);
      toast(messages.length ? 'The file was not imported.' : (err as Error).message, 'bad');
    } finally {
      setFileBusy(null);
    }
  }

  const version = state?.version ?? 0;
  const live = version > 0;
  const hasDraft = state?.hasDraft ?? false;
  const liveDesign = state ? designLabel(withDefaults(state.live, withDefaults(state.defaults)).template) : '';

  if (banners.length === 0) return <div className="empty">No cookie banner.</div>;

  return (
    <>
      <div className="ck-facts ck-banner-strip">
        {banners.length > 1 && (
          <select value={banner?.id ?? ''} onChange={(e) => pick(e.target.value)} aria-label="Banner">
            {banners.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        {banners.length === 1 && <strong>{banner?.name}</strong>}
        <span>
          {live
            ? <>Live v{version} · {liveDesign} · published {day(state?.publishedAt)}{state?.publishedBy ? ` by ${state.publishedBy}` : ''}</>
            : <>Not published yet · the banner shows the standard look</>}
          {hasDraft && <> · Draft has changes</>}
          {dirty && <> · Unsaved changes</>}
        </span>
        {editable && (
          <div className="ck-banner-actions">
            <button onClick={() => setDiscarding(true)} disabled={busy !== null || (!hasDraft && !dirty)}>Discard draft</button>
            <button onClick={() => void saveDraft()} disabled={busy !== null || !dirty || saveBlocked || !state}>
              {busy === 'save' ? 'Saving…' : `Save draft${count(issues.length + templateIssues.length)}`}
            </button>
            <button className="primary" onClick={() => setPublishing(true)} disabled={busy !== null || publishBlocked || !state || (!dirty && !hasDraft)}>
              Publish{count(issues.length + templateIssues.length + previewIssues.length)}
            </button>
          </div>
        )}
      </div>

      <ErrorNote error={loadError} />
      {issues.length > 0 && <div className="note bad" style={{ marginBottom: 14 }}>{issues.join(' ')}</div>}
      {importIssues.length > 0 && (
        <div className="note bad" style={{ marginBottom: 14 }} role="alert">
          <strong>The file was not imported.</strong>
          <ul className="ck-issue-list">{importIssues.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </div>
      )}

      {banner && (
        <div className="ck-banner">
          <div className="ck-banner-form">
            {editing ? (
              <DesignEditor
                a={form} update={update} readOnly={!editable}
                issues={templateIssues} checking={checking} previewIssues={previewIssues}
                onApply={applyEditor} onDiscard={discardEditor} onReset={() => void resetTemplate()}
              />
            ) : (
              <>
                <DesignCard
                  a={form} update={update} readOnly={!editable}
                  issueCount={templateIssues.length} busy={fileBusy}
                  onEdit={() => void openEditor()} onReset={() => void resetTemplate()}
                  onExport={() => void exportBanner()} onImport={(f) => void importBanner(f)}
                />
                <LayoutCard a={form} update={update} readOnly={!editable} />
                <ButtonsCard a={form} update={update} readOnly={!editable} />
                <TextCard a={form} update={update} readOnly={!editable} languages={languages} lang={lang} onLang={setLang} />
                <LookCard a={form} update={update} readOnly={!editable} logoUrl={logoUrl} onLogo={setLogoUrl} />
                <ReopenCard a={form} update={update} readOnly={!editable} />
                <BehaviourCard banner={banner} onChanged={onChanged} />
                <Regions banner={banner} onChanged={onChanged} />
              </>
            )}
          </div>
          <div className="ck-banner-side">
            <Preview
              pointId={banner.id}
              appearance={form}
              lang={lang}
              languages={languages}
              onLang={setLang}
              onLanguages={(ls) => { setLanguages(ls); if (!ls.some((l) => l.code === lang)) setLang(ls[0]?.code ?? 'en'); }}
              onIssues={setPreviewIssues}
            />
          </div>
        </div>
      )}

      {publishing && (
        <Modal
          title={`Publish banner v${version + 1}`}
          onClose={() => { if (busy !== 'publish') setPublishing(false); }}
          footer={
            <>
              <button onClick={() => setPublishing(false)} disabled={busy === 'publish'}>Cancel</button>
              <button className="primary" onClick={() => void publish()} disabled={busy === 'publish'}>{busy === 'publish' ? 'Publishing…' : 'Publish'}</button>
            </>
          }
        >
          <p>Goes live on the next page load. Every consent recorded after this carries this version.</p>
          <p className="muted small">Design: {designLabel(form.template)}.</p>
        </Modal>
      )}

      {discarding && (
        <Modal
          title="Discard draft"
          onClose={() => { if (busy !== 'discard') setDiscarding(false); }}
          footer={
            <>
              <button onClick={() => setDiscarding(false)} disabled={busy === 'discard'}>Cancel</button>
              <button className="danger" onClick={() => void discard()} disabled={busy === 'discard'}>{busy === 'discard' ? 'Discarding…' : 'Discard'}</button>
            </>
          }
        >
          <p>{live ? `The form goes back to the live banner, v${version}.` : 'The form goes back to the standard look.'}</p>
        </Modal>
      )}
    </>
  );
}
